import type { ReactNode } from "react";

import type { BusbarEvidenceRunCreateRequest } from "../services/busbarEvidence";
import { downloadRunJson } from "../services/runExport";
import { BusbarEvidenceForm } from "../components/BusbarEvidenceForm";
import { BusbarEvidenceResultPanel } from "../components/BusbarEvidenceResultPanel";
import { BusbarEvidenceResultSummary } from "../components/BusbarEvidenceResultSummary";
import { BusbarEvidenceWarningPanel } from "../components/BusbarEvidenceWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useBusbarEvidence } from "../hooks/useBusbarEvidence";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The busbar evidence study could not be completed.";
}

type BusbarEvidencePageProps = {
  // The switch between the two busbar modes; the host page (BusbarPage) puts it in the header.
  // Without it the page is exactly the single page it was.
  modePicker?: ReactNode;
};

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
// EOS-07 is delivered in parts (A24); this page is 07a, the module's first. The mode switch
// to 07c is added by the host page through modePicker.
export function BusbarEvidencePage({ modePicker }: BusbarEvidencePageProps = {}) {
  const { calculate, reset, result, run, error, isPending, isError } = useBusbarEvidence();
  // The active revision's single-line diagram, for the bus picker (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: BusbarEvidenceRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-busbar-evidence-page>
      <header>
        <p>EOS-07 · Panels and switchboards</p>
        <h1>Busbar evidence check</h1>
        <p>
          Check a panel busbar or busbar trunking against its builder-declared, design-verified
          ratings (IEC 61439): rated current, short-time withstand Icw with its duration, peak
          withstand Ipk, neutral and trunking voltage drop, using the fault level of a stored
          short-circuit study of the same project revision. The busbar itself is proven only by
          the builder's design verification.
        </p>
        {modePicker}
      </header>

      <div data-study-layout>
        <section aria-labelledby="busbar-evidence-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="busbar-evidence-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <BusbarEvidenceForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="busbar-evidence-results-heading" data-study-results>
          <h2 id="busbar-evidence-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the busbar evidence…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <BusbarEvidenceResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <BusbarEvidenceWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <BusbarEvidenceResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to check the busbar evidence.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="busbar-evidence-review-heading">
        <h2 id="busbar-evidence-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the linked fault study, the design current, the fault clearing
          time and every builder-declared rating against the design verification before
          approval or issue.
        </p>
      </section>
    </main>
  );
}

export default BusbarEvidencePage;
